import { useMuiModalContext } from '@modules/common/components/Modal/ModalContext';
import { useIsArtefactEditable } from '@modules/common/hooks';
import { Direction } from '@modules/common/types';
import { RemoveTextWidgetDialogContent } from '@modules/dashboard/components/TextWidget/RemoveTextWidgetDialogContent';
import { useAddTextWidget } from '@modules/dashboard/hooks';
import { useDashboardStore } from '@modules/dashboard/store/useDashboardStore';
import { RemoveCircleRounded } from '@mui/icons-material';
import ControlPointDuplicateRoundedIcon from '@mui/icons-material/ControlPointDuplicateRounded';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import {
  Box,
  BoxProps,
  IconButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
} from '@mui/material';
import { DashboardCell } from 'generated/graphql';
import { useState } from 'react';
import scrollIntoView from 'scroll-into-view-if-needed';

interface TextWidgetActionsProps {
  className?: string;
  sx?: BoxProps['sx'];
  cell: DashboardCell;
  isEditorActive: boolean;
  rowID: string;
  textContent: string;
}

export const TextWidgetActions = ({
  sx,
  cell,
  rowID,
  className,
  textContent,
  isEditorActive,
}: TextWidgetActionsProps) => {
  const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null);
  const dashboard = useDashboardStore(({ dashboard }) => dashboard);
  const metadata = useDashboardStore(({ metadata }) => metadata);

  const { openModal } = useMuiModalContext();

  const addTextWidget = useAddTextWidget({
    rowID,
    cellInsertDirection: Direction.Right,
  });

  const handleMenuClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setMenuAnchor(event.currentTarget);
  };

  const handleMenuClose = () => {
    setMenuAnchor(null);
  };

  const isEditable = useIsArtefactEditable({
    editToken: dashboard.editToken,
    status: dashboard.status,
  });

  const handleRemove = () => {
    if (!dashboard || !isEditable) {
      return;
    }
    openModal({
      contentComponent: () => (
        <RemoveTextWidgetDialogContent metadata={metadata} cell={cell} />
      ),
      modalProps: {
        fullWidth: true,
        maxWidth: 'sm',
        PaperProps: {
          elevation: 3,
        },
      },
    });
    handleMenuClose();
  };

  const handleDuplicate = async () => {
    if (!dashboard || !isEditable) {
      return;
    }

    handleMenuClose();
    const newCell = await addTextWidget(textContent);
    const cellElement = document.getElementById(newCell.id);

    if (cellElement) {
      scrollIntoView(cellElement, {
        scrollMode: 'if-needed',
        block: 'nearest',
        inline: 'nearest',
        behavior: 'smooth',
      });
    }
  };

  if (!isEditable) {
    return null;
  }

  const isMenuOpen = Boolean(menuAnchor);

  return (
    <Box
      className={className}
      sx={{
        ...sx,
        visibility: isEditorActive || isMenuOpen ? 'visible' : 'hidden',
      }}
    >
      <IconButton size="small" onClick={handleMenuClick}>
        <MoreVertIcon fontSize="small" />
      </IconButton>
      <Menu anchorEl={menuAnchor} open={isMenuOpen} onClose={handleMenuClose}>
        <MenuItem onClick={handleDuplicate}>
          <ListItemIcon>
            <ControlPointDuplicateRoundedIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Duplicate</ListItemText>
        </MenuItem>
        <MenuItem onClick={handleRemove}>
          <ListItemIcon>
            <RemoveCircleRounded fontSize="small" />
          </ListItemIcon>
          <ListItemText>Remove from Dashboard</ListItemText>
        </MenuItem>
      </Menu>
    </Box>
  );
};
